// O calendário tributário: feriados, dia útil e a REGRA que gera a data de cada
// obrigação.
//
// Puro, sem UM import, com prova própria (`app/tests/calendario-fiscal.mjs`).
//
// ---------------------------------------------------------------------------
// Por que este módulo existe: o app de origem guardava a RESPOSTA
// ---------------------------------------------------------------------------
// No `calendario-tributario` da Lovable, cada obrigação é uma linha com DATA
// ABSOLUTA. Medido em 24/08 nas 62 linhas do catálogo dele:
//
//   · cobrem 2026-05-07 a 2026-09-30 e param ali — maio 16, junho 15, julho 15,
//     agosto 14, **setembro 2**. Alguém digitava ~15 linhas por mês, e parou;
//   · são **20 obrigações distintas**; 14 são mensais e cobrem 56 das 62 linhas
//     (90%), as outras 6 aparecem uma vez só.
//
// (Este número já foi contado errado duas vezes, e o motivo é instrutivo: a
// competência aparece no título de dois jeitos. "Dirbi – Abril/2026" a põe no
// fim; o eSocial a põe DENTRO dos parênteses — "eSocial S-1299 (Evento
// Fechamento de Folha - mês abril)". Tirando só a do fim, dá 22; tirando as
// duas, 20. Nem esse título é confiável: a linha de agosto diz "mês Junho",
// igual à de julho, então a origem afirma a mesma competência com dois
// vencimentos. O cabeçalho da 000106 ainda carrega o 22, porque migration
// aplicada não se edita.)
//
// Ou seja: o conhecimento tributário morava na cabeça de quem digitava, e o
// banco guardava só o resultado. A consequência apareceu em junho de 2026, mês
// em que o **dia 20 caiu num sábado**. As sete obrigações do dia 20 receberam
// TRÊS tratamentos diferentes:
//
//   DAS e PGDAS-D      -> 22 (segunda)   prorrogou
//   DARF-INSS, FGTS,
//   DCTFWeb, DAE       -> 19 (sexta)     antecipou
//   Dirbi              -> 20 (SÁBADO)    não mexeu
//
// Os dois primeiros são a regra tributária real. O terceiro é um esquecimento —
// 2 das 62 datas caem em dia não útil. Não é desleixo de ninguém: é o resultado
// previsível de um sistema que guarda a resposta em vez da regra.
//
// Aqui a regra é o dado, e a data é DERIVADA. A ferramenta deixa de expirar, e o
// conhecimento passa a estar no sistema.
//
// ---------------------------------------------------------------------------
// Os feriados NACIONAIS são calculados, e a lista é a da LEI
// ---------------------------------------------------------------------------
// Carnaval e Corpus Christi **não são feriados nacionais** — são ponto
// facultativo. Isso não é detalhe acadêmico: em junho de 2026 a Corpus Christi
// cai numa quinta (04/06), e se ela contasse como feriado o 5º dia útil de junho
// mudaria. O admin do app de origem não a contou, e é por isso que a inferência
// só fechou quando usei a lista legal — nas quatro competências, "Pagamento
// Salário" fecha como 5º dia útil COM sábado e SEM Corpus Christi.
//
// A lista legal (Lei 662/1949, Lei 6.802/1980, Lei 10.607/2002 e Lei
// 14.759/2023, que tornou a Consciência Negra nacional a partir de 2024) tem dez
// entradas, das quais uma é móvel: a Paixão de Cristo, dois dias antes da Páscoa.
//
// Feriado municipal, estadual e ponto facultativo que a casa queira tratar como
// feriado entram por EXCEÇÃO, do banco — não aqui.
//
// ---------------------------------------------------------------------------
// Datas são STRING 'AAAA-MM-DD', e a aritmética é toda em UTC
// ---------------------------------------------------------------------------
// `new Date("2026-01-01")` é meia-noite UTC, e em UTC-3 imprime 31/12/2025. Este
// módulo nunca constrói `Date` a partir de string solta: usa `Date.UTC` com
// ano/mês/dia separados, e converte de volta com aritmética própria. É o mesmo
// cuidado que o hub tem com o resto: nenhuma data do sistema depende do fuso de
// quem abre a tela.

// ---------------------------------------------------------------------------
// Aritmética de data
// ---------------------------------------------------------------------------

const DIA_EM_MS = 86400000;

/** 'AAAA-MM-DD' → o número de dias desde a época, em UTC. */
function paraDias(data: string): number {
  const [a, m, d] = data.split("-").map(Number);
  return Math.floor(Date.UTC(a, m - 1, d) / DIA_EM_MS);
}

/** O caminho de volta. */
function paraData(dias: number): string {
  const d = new Date(dias * DIA_EM_MS);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}

export function dataDe(ano: number, mes: number, dia: number): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${ano}-${p(mes)}-${p(dia)}`;
}

export function somarDias(data: string, n: number): string {
  return paraData(paraDias(data) + n);
}

/** 0 = domingo … 6 = sábado. */
export function diaDaSemana(data: string): number {
  // A época (1970-01-01) foi uma quinta-feira: 4.
  return (((paraDias(data) + 4) % 7) + 7) % 7;
}

export function ehFimDeSemana(data: string): boolean {
  const d = diaDaSemana(data);
  return d === 0 || d === 6;
}

export function anoDe(data: string): number {
  return Number(data.slice(0, 4));
}
export function mesDe(data: string): number {
  return Number(data.slice(5, 7));
}
export function diaDe(data: string): number {
  return Number(data.slice(8, 10));
}

/** Quantos dias tem o mês. */
export function diasNoMes(ano: number, mes: number): number {
  return new Date(Date.UTC(ano, mes, 0)).getUTCDate();
}

// ---------------------------------------------------------------------------
// Páscoa e feriados nacionais
// ---------------------------------------------------------------------------

/**
 * O domingo de Páscoa, pelo algoritmo de Meeus/Jones/Butcher (gregoriano).
 *
 * A Páscoa importa aqui por UM feriado: a Paixão de Cristo, dois dias antes.
 * Carnaval (47 e 48 dias antes) e Corpus Christi (60 dias depois) NÃO entram —
 * são ponto facultativo.
 */
export function pascoa(ano: number): string {
  const a = ano % 19;
  const b = Math.floor(ano / 100);
  const c = ano % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31);
  const dia = ((h + l - 7 * m + 114) % 31) + 1;
  return dataDe(ano, mes, dia);
}

export type Feriado = { data: string; nome: string; nacional: boolean };

/**
 * Os feriados nacionais do ano, pela LEI.
 *
 * Dez entradas de 2024 em diante (a Consciência Negra virou nacional na Lei
 * 14.759/2023); nove antes disso. A Paixão de Cristo é a única móvel.
 */
export function feriadosNacionais(ano: number): Feriado[] {
  const lista: Feriado[] = [
    { data: dataDe(ano, 1, 1), nome: "Confraternização Universal", nacional: true },
    { data: somarDias(pascoa(ano), -2), nome: "Paixão de Cristo", nacional: true },
    { data: dataDe(ano, 4, 21), nome: "Tiradentes", nacional: true },
    { data: dataDe(ano, 5, 1), nome: "Dia do Trabalho", nacional: true },
    { data: dataDe(ano, 9, 7), nome: "Independência do Brasil", nacional: true },
    { data: dataDe(ano, 10, 12), nome: "Nossa Senhora Aparecida", nacional: true },
    { data: dataDe(ano, 11, 2), nome: "Finados", nacional: true },
    { data: dataDe(ano, 11, 15), nome: "Proclamação da República", nacional: true },
    { data: dataDe(ano, 12, 25), nome: "Natal", nacional: true },
  ];
  // Lei 14.759, de 21/12/2023: o Dia Nacional de Zumbi e da Consciência Negra
  // passou a ser feriado nacional. Antes de 2024 não era — e o calendário do
  // hub precisa acertar o passado também, porque o aluno consulta ano anterior.
  if (ano >= 2024) {
    lista.push({ data: dataDe(ano, 11, 20), nome: "Consciência Negra", nacional: true });
  }
  return lista.sort((x, y) => x.data.localeCompare(y.data));
}

/**
 * As exceções que vêm do banco: o que ACRESCENTAR (feriado municipal, estadual,
 * ponto facultativo que a casa decida tratar como feriado) e o que REMOVER.
 *
 * Remover existe porque acontece: em ano de eleição ou por decreto, um feriado
 * é transferido. Sem isso, a única saída seria mexer no código — e aí a
 * ferramenta volta a expirar sozinha, que é o defeito que este módulo existe
 * para consertar.
 */
export type ExcecoesDeFeriado = {
  acrescentar?: Feriado[];
  remover?: string[];
};

export type Calendario = {
  /** Toda data de feriado, para consulta rápida. */
  feriados: Map<string, Feriado>;
};

export function montarCalendario(ano: number, excecoes?: ExcecoesDeFeriado): Calendario {
  const mapa = new Map<string, Feriado>();
  for (const f of feriadosNacionais(ano)) mapa.set(f.data, f);
  for (const f of excecoes?.acrescentar ?? []) {
    if (anoDe(f.data) === ano) mapa.set(f.data, f);
  }
  for (const d of excecoes?.remover ?? []) mapa.delete(d);
  return { feriados: mapa };
}

export function ehFeriado(cal: Calendario, data: string): boolean {
  return cal.feriados.has(data);
}

// ---------------------------------------------------------------------------
// Dia útil — e as DUAS definições, que não são a mesma
// ---------------------------------------------------------------------------
// `bancario`: segunda a sexta, fora de feriado. É o que vale para vencimento de
// tributo, porque o pagamento passa por banco.
//
// `clt`: sábado CONTA. É o que vale para o "5º dia útil" do art. 459 da CLT, o
// prazo de pagamento do salário. Medido: as quatro competências de "Pagamento
// Salário" do app de origem só fecham com esta definição.
//
// Chamar as duas de "dia útil" e usar uma só seria errar o salário de todo mês
// que começa em sábado.

export type TipoDeDiaUtil = "bancario" | "clt";

export function ehDiaUtil(cal: Calendario, data: string, tipo: TipoDeDiaUtil = "bancario"): boolean {
  if (ehFeriado(cal, data)) return false;
  const d = diaDaSemana(data);
  if (d === 0) return false;
  if (d === 6) return tipo === "clt";
  return true;
}

/** O dia útil anterior (nunca devolve a própria data). */
export function diaUtilAnterior(cal: Calendario, data: string): string {
  let d = somarDias(data, -1);
  // Guarda de laço: um ano inteiro de feriados seguidos é impossível, e um
  // `while (true)` sobre dado ruim travaria a tela do aluno.
  for (let i = 0; i < 400; i++) {
    if (ehDiaUtil(cal, d)) return d;
    d = somarDias(d, -1);
  }
  return d;
}

/** O dia útil seguinte (nunca devolve a própria data). */
export function diaUtilSeguinte(cal: Calendario, data: string): string {
  let d = somarDias(data, 1);
  for (let i = 0; i < 400; i++) {
    if (ehDiaUtil(cal, d)) return d;
    d = somarDias(d, 1);
  }
  return d;
}

export function ultimoDiaUtilDoMes(cal: Calendario, ano: number, mes: number): string {
  let d = dataDe(ano, mes, diasNoMes(ano, mes));
  for (let i = 0; i < 40; i++) {
    if (ehDiaUtil(cal, d)) return d;
    d = somarDias(d, -1);
  }
  return d;
}

/**
 * O n-ésimo dia útil do mês. Devolve `null` quando o mês não tem tantos —
 * fevereiro tem 19 ou 20 dias úteis bancários, então pedir o 25º é pergunta sem
 * resposta, e inventar o último dia seria mentir.
 */
export function nEsimoDiaUtil(
  cal: Calendario,
  ano: number,
  mes: number,
  n: number,
  tipo: TipoDeDiaUtil = "bancario",
): string | null {
  if (n < 1) return null;
  let contados = 0;
  const total = diasNoMes(ano, mes);
  for (let dia = 1; dia <= total; dia++) {
    const d = dataDe(ano, mes, dia);
    if (ehDiaUtil(cal, d, tipo)) {
      contados += 1;
      if (contados === n) return d;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// A REGRA de vencimento
// ---------------------------------------------------------------------------
// Três formas, e todas as três foram MEDIDAS no catálogo do app de origem —
// nenhuma é hipótese. Das 20 obrigações:
//
//   dia-fixo         11 (dia 15, 20 e 31), sendo 9 com ajuste e 2 sem
//   n-dia-util        3 — "Pagamento Salário" é o 5º dia útil COM sábado (art.
//                     459 da CLT), e a EFD-Contribuições o 10º SEM. Contar
//                     sábado é a exceção, não o padrão: as duas leituras
//                     divergem em 7 dos 24 meses de 2026-2027
//   ultimo-dia-util   6 — "Parcelamento", "DCTFWeb", ECD, ECF, DIRPF e a opção
//                     pelo Simples. É a redação legal, e é idêntica a
//                     "dia 31, antecipar" por causa da trava do dia
//
//   As 2 com ajuste "nenhum" (Dirbi e DASN-Simei) vencem em dia NÃO ÚTIL. Ou é
//   a regra delas, ou é o erro que a digitação à mão produziu — o ETL as marca
//   para a equipe em vez de decidir sozinho.
//
// E o AJUSTE é por obrigação, nunca global. Junho de 2026 provou: mesmo dia
// nominal, mesmo sábado, três tratamentos. Uma regra global estaria errada para
// dois terços das obrigações do dia 20.

export type Ajuste = "antecipar" | "prorrogar" | "nenhum";

/**
 * As três formas de regra — e são exatamente as três que
 * `calendario_obrigacoes.regra_tipo` aceita, de propósito.
 *
 * Aqui havia uma quarta, `data-fixa`, para o decreto que crava a data de uma
 * competência. Foi removida em 25/08 por ser a TERCEIRA representação da mesma
 * coisa: o decreto mora em `calendario_excecoes` (uma linha por competência), e
 * a constraint do banco nunca aceitou `data-fixa` como `regra_tipo`. Ou seja: a
 * variante existia no tipo, era tratada em duas funções, e não podia chegar do
 * catálogo por nenhum caminho. Quem aplica decreto agora é
 * `vencimentoDaObrigacao`, abaixo.
 */
export type Regra =
  | { tipo: "dia-fixo"; dia: number; ajuste: Ajuste }
  | { tipo: "n-dia-util"; n: number; diaUtil: TipoDeDiaUtil }
  | { tipo: "ultimo-dia-util" };

/** Mensal, ou anual num mês só. */
export type Periodicidade =
  | { tipo: "mensal" }
  | { tipo: "anual"; mes: number };

export function ajustar(cal: Calendario, data: string, ajuste: Ajuste): string {
  if (ehDiaUtil(cal, data)) return data;
  if (ajuste === "antecipar") return diaUtilAnterior(cal, data);
  if (ajuste === "prorrogar") return diaUtilSeguinte(cal, data);
  return data;
}

/**
 * O que ACONTECEU com a data nominal daquele mês.
 *
 * Existe porque a tese desta ferramenta é justamente esta, e ela estava
 * invisível: em junho de 2026 o dia 20 caiu num sábado, e o app de origem deu
 * três destinos ao mesmo dia. A tela sabia dizer a REGRA ("dia 20, prorrogado se
 * cair em dia não útil") mas não sabia dizer o FATO daquele mês ("o dia 20 caiu
 * num sábado, então vence dia 22").
 *
 * E este dado nasce AQUI, na função que escolhe a data, nunca remontado pela
 * tela a partir do resultado. É a lição da 000091: quem decide o número decide o
 * porquê — explicação calculada por fora é uma segunda definição da mesma coisa,
 * e foi assim que o teto do MEI passou a se contradizer em quatro situações.
 * Recalcular "o nominal era 20 e virou 22" fora daqui exigiria repetir a trava
 * do dia 31 e a aritmética de dia útil.
 */
export type Derivacao = {
  /** O dia que a regra pediu, antes de qualquer ajuste. Nulo se não houve dia nominal. */
  nominal: string | null;
  /** Houve mudança? Só é `true` quando o nominal existia e a data final difere dele. */
  ajustada: boolean;
  /** Para onde foi, quando foi. */
  sentido: "antecipou" | "prorrogou" | null;
  /**
   * A data final caiu em dia NÃO ÚTIL — o estado que o app de origem produzia
   * por esquecimento (o Dirbi no sábado, a DASN-Simei num domingo). Verdadeiro
   * também quando o ajuste é "nenhum" de propósito.
   */
  emDiaNaoUtil: boolean;
};

export type DataDerivada = { data: string | null; derivacao: Derivacao };

const SEM_DERIVACAO: Derivacao = {
  nominal: null,
  ajustada: false,
  sentido: null,
  emDiaNaoUtil: false,
};

/**
 * A data da obrigação naquele mês, com a derivação ao lado — ou `null` quando a
 * regra não produz data ali (obrigação anual fora do mês dela, ou n-ésimo dia
 * útil que o mês não tem).
 *
 * Devolver `null` em vez de uma data aproximada é a decisão que importa: um
 * calendário tributário com data aproximada é pior que um calendário sem a
 * linha, porque o contador confia nele.
 */
export function derivarDataDaObrigacao(
  cal: Calendario,
  regra: Regra,
  periodicidade: Periodicidade,
  ano: number,
  mes: number,
): DataDerivada {
  if (periodicidade.tipo === "anual" && periodicidade.mes !== mes) {
    return { data: null, derivacao: SEM_DERIVACAO };
  }

  // Nas duas formas que já nascem em dia útil não há nominal a comparar: o
  // "último dia útil" e o "n-ésimo dia útil" são o resultado, não um dia
  // escolhido e depois corrigido. Dizer "ajustada" aqui seria inventar um fato.
  if (regra.tipo === "ultimo-dia-util") {
    return { data: ultimoDiaUtilDoMes(cal, ano, mes), derivacao: SEM_DERIVACAO };
  }
  if (regra.tipo === "n-dia-util") {
    return { data: nEsimoDiaUtil(cal, ano, mes, regra.n, regra.diaUtil), derivacao: SEM_DERIVACAO };
  }

  // dia-fixo. Dia 31 em fevereiro não existe: o nominal passa a ser o último dia
  // do mês, e só então o ajuste roda. Sem isto, "dia 31, antecipar" em fevereiro
  // daria 3 de março antecipado — uma data no mês seguinte.
  const total = diasNoMes(ano, mes);
  const nominal = dataDe(ano, mes, Math.min(regra.dia, total));
  const data = ajustar(cal, nominal, regra.ajuste);
  return {
    data,
    derivacao: {
      nominal,
      ajustada: data !== nominal,
      sentido: data === nominal ? null : data < nominal ? "antecipou" : "prorrogou",
      emDiaNaoUtil: !ehDiaUtil(cal, data),
    },
  };
}

/**
 * Só a data. Continua existindo porque é o que a prova cobra caso por caso e o
 * que o ETL usa para inferir regra — ali a derivação não interessa.
 */
export function dataDaObrigacao(
  cal: Calendario,
  regra: Regra,
  periodicidade: Periodicidade,
  ano: number,
  mes: number,
): string | null {
  return derivarDataDaObrigacao(cal, regra, periodicidade, ano, mes).data;
}

// ---------------------------------------------------------------------------
// A exceção por decreto — uma linha por competência
// ---------------------------------------------------------------------------
/**
 * Uma linha de `calendario_excecoes`: o que o decreto disse sobre UMA
 * competência daquela obrigação.
 *
 * `data: null` quer dizer **não vence nesta competência** — e é um estado
 * necessário, não um descuido de modelagem: quando um decreto move a DIRPF de
 * maio para junho, junho recebe a data nova e maio recebe a suspensão. Sem a
 * suspensão, a obrigação apareceria nos dois meses.
 */
export type ExcecaoDeCompetencia = {
  ano: number;
  mes: number;
  data: string | null;
  motivo: string;
};

/** O vencimento, quem o decidiu, e o que aconteceu com a data nominal. */
export type Vencimento = {
  data: string | null;
  /** Preenchido só quando foi um decreto que decidiu — a tela precisa dizer. */
  excecao: ExcecaoDeCompetencia | null;
  /**
   * Como a data saiu da regra. Quando o decreto decide, a derivação vem vazia:
   * ali não houve ajuste de dia útil nenhum, houve uma data imposta — e dizer
   * "prorrogou" sobre um decreto seria atribuir à regra o que foi da norma.
   */
  derivacao: Derivacao;
};

export function chaveDaCompetencia(ano: number, mes: number): string {
  return `${ano}-${String(mes).padStart(2, "0")}`;
}

export function indexarExcecoes(
  lista: ExcecaoDeCompetencia[],
): Map<string, ExcecaoDeCompetencia> {
  const mapa = new Map<string, ExcecaoDeCompetencia>();
  for (const e of lista) mapa.set(chaveDaCompetencia(e.ano, e.mes), e);
  return mapa;
}

/**
 * O vencimento de uma obrigação numa competência, com o decreto aplicado.
 *
 * O decreto vence a regra SEMPRE dentro da competência dele, inclusive para
 * criar data onde a regra não produz nenhuma — é assim que se representa uma
 * obrigação anual adiada para outro mês. Fora da competência dele, ele não
 * existe: por isso a busca é por chave exata, e não por proximidade.
 *
 * Esta é a única função que a tela deve chamar para saber "quando vence?". A
 * `dataDaObrigacao` continua exportada porque é ela que a prova cobra caso por
 * caso, mas quem a chamar direto perde o decreto em silêncio.
 */
export function vencimentoDaObrigacao(
  cal: Calendario,
  regra: Regra,
  periodicidade: Periodicidade,
  ano: number,
  mes: number,
  excecoes?: Map<string, ExcecaoDeCompetencia>,
): Vencimento {
  const decreto = excecoes?.get(chaveDaCompetencia(ano, mes));
  if (decreto) {
    return {
      data: decreto.data,
      excecao: decreto,
      // Um decreto pode cravar a data num sábado, e a tela precisa poder dizer
      // isso — mas sem chamar de "ajuste", que é da regra.
      derivacao: {
        ...SEM_DERIVACAO,
        emDiaNaoUtil: decreto.data ? !ehDiaUtil(cal, decreto.data) : false,
      },
    };
  }
  const { data, derivacao } = derivarDataDaObrigacao(cal, regra, periodicidade, ano, mes);
  return { data, excecao: null, derivacao };
}

/**
 * O glifo do ajuste, em texto: `"sáb 20 → seg 22"`.
 *
 * Sai daqui, e não da tela, pelo mesmo motivo da `Derivacao`: é a leitura do
 * mesmo fato. Devolve `null` quando não houve ajuste — e aí a tela não desenha
 * nada, em vez de desenhar um glifo vazio.
 */
export function glifoDoAjuste(v: Vencimento): string | null {
  if (!v.derivacao.ajustada || !v.derivacao.nominal || !v.data) return null;
  const curto = (d: string) => `${NOMES_DOS_DIAS[diaDaSemana(d)].toLowerCase()} ${diaDe(d)}`;
  return `${curto(v.derivacao.nominal)} → ${curto(v.data)}`;
}

/** A frase inteira, para leitor de tela e para o `title` do glifo. */
export function fraseDoAjuste(v: Vencimento): string | null {
  if (!v.derivacao.ajustada || !v.derivacao.nominal || !v.data) return null;
  const dia = diaDe(v.derivacao.nominal);
  const semana = NOMES_DOS_DIAS_LONGOS[diaDaSemana(v.derivacao.nominal)];
  const verbo = v.derivacao.sentido === "antecipou" ? "antecipado" : "prorrogado";
  const artigo = semana === "sábado" || semana === "domingo" ? "num" : "numa";
  return `o dia ${dia} caiu ${artigo} ${semana}, então o prazo foi ${verbo} para ${dataBr(v.data)}`;
}

/** O texto que explica a regra ao lado da data, na tela. */
export function explicarRegra(regra: Regra, periodicidade: Periodicidade): string {
  const quando =
    periodicidade.tipo === "anual"
      ? ` (uma vez por ano, em ${NOMES_DOS_MESES[periodicidade.mes - 1].toLowerCase()})`
      : "";

  if (regra.tipo === "ultimo-dia-util") return `último dia útil do mês${quando}`;
  if (regra.tipo === "n-dia-util") {
    const como =
      regra.diaUtil === "clt"
        ? " (contando sábado, como manda o art. 459 da CLT)"
        : "";
    return `${regra.n}º dia útil do mês${como}${quando}`;
  }
  const ajuste =
    regra.ajuste === "antecipar"
      ? ", antecipado para o dia útil anterior se cair em dia não útil"
      : regra.ajuste === "prorrogar"
        ? ", prorrogado para o dia útil seguinte se cair em dia não útil"
        : ", sem ajuste quando cai em dia não útil";
  return `dia ${regra.dia}${ajuste}${quando}`;
}

// ---------------------------------------------------------------------------
// A grade do mês
// ---------------------------------------------------------------------------

export const NOMES_DOS_MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

export const NOMES_DOS_DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

/**
 * Os nomes inteiros, para FRASE.
 *
 * A abreviação serve ao glifo (`sáb 20 → seg 22`), onde o espaço é escasso e o
 * leitor está varrendo; a frase que o leitor de tela lê não pode dizer "caiu num
 * sáb". Duas listas porque são dois usos, não porque uma esqueceu a outra.
 */
export const NOMES_DOS_DIAS_LONGOS = [
  "domingo",
  "segunda-feira",
  "terça-feira",
  "quarta-feira",
  "quinta-feira",
  "sexta-feira",
  "sábado",
];

export type CelulaDoMes = {
  data: string;
  dia: number;
  doMes: boolean;
  fimDeSemana: boolean;
  feriado: Feriado | null;
  hoje: boolean;
};

/**
 * A grade de seis semanas do mês, começando no domingo.
 *
 * Seis semanas FIXAS, e não "quantas couberem": grade que muda de altura entre
 * meses faz a tela pular quando o aluno navega, e o dia que ele estava olhando
 * troca de lugar. Um mês nunca precisa de mais de seis semanas.
 *
 * `hoje` entra como parâmetro em vez de sair de `new Date()` para a função poder
 * ser provada com qualquer data.
 */
export function gradeDoMes(
  cal: Calendario,
  ano: number,
  mes: number,
  hoje: string,
): CelulaDoMes[] {
  const primeiro = dataDe(ano, mes, 1);
  const comeco = somarDias(primeiro, -diaDaSemana(primeiro));
  const celulas: CelulaDoMes[] = [];
  for (let i = 0; i < 42; i++) {
    const data = somarDias(comeco, i);
    celulas.push({
      data,
      dia: diaDe(data),
      doMes: mesDe(data) === mes && anoDe(data) === ano,
      fimDeSemana: ehFimDeSemana(data),
      feriado: cal.feriados.get(data) ?? null,
      hoje: data === hoje,
    });
  }
  return celulas;
}

/** Navegação de mês, sem cair em mês 0 ou 13. */
export function mesAnterior(ano: number, mes: number): { ano: number; mes: number } {
  return mes === 1 ? { ano: ano - 1, mes: 12 } : { ano, mes: mes - 1 };
}
export function mesSeguinte(ano: number, mes: number): { ano: number; mes: number } {
  return mes === 12 ? { ano: ano + 1, mes: 1 } : { ano, mes: mes + 1 };
}

/** 'AAAA-MM-DD' → '24/08/2026'. */
export function dataBr(data: string): string {
  return `${data.slice(8, 10)}/${data.slice(5, 7)}/${data.slice(0, 4)}`;
}
